import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18nContext";
import { api } from "@/lib/api";
import Seo, { personSchema } from "@/components/Seo";
import {
  Music2, Mountain, Bike, Gamepad2,
  ArrowUpRight, Check, MapPin, Languages, Award, Briefcase,
  Trophy, Plane, Users, Film, Heart, ShieldCheck, Sparkles, Download,
} from "lucide-react";

// Match by German key (canonical name) because hobbies are stored as multilingual objects
const HOBBY_ICONS = {
  "Tennis": Trophy,
  "Tischtennis": Trophy,
  "Schwimmen": Trophy,
  "Fußball": Trophy,
  "Jeet Kune Do": ShieldCheck,
  "Musik (hören & machen)": Music2,
  "Reisen": Plane,
  "Filme & Serien": Film,
  "Motorradfahren": Bike,
  "Wandern": Mountain,
  "League of Legends": Gamepad2,
  "Counter-Strike": Gamepad2,
};

const CERT_ICONS = { ShieldCheck, Sparkles, Award };

export default function AboutPage() {
  const { t, tl, pathWithLang } = useI18n();
  const [profile, setProfile] = useState(null);
  const [certifications, setCertifications] = useState([]);

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/certifications").then((r) => setCertifications(r.data || [])).catch(() => {});
  }, []);

  if (!profile) return null;

  return (
    <div data-testid="about-page">
      <Seo
        title={t("seo.about.title")}
        description={t("seo.about.description")}
        type="profile"
        jsonLd={personSchema}
      />
      {/* HERO */}
      <section className="relative noise overflow-hidden">
        <div className="absolute inset-0 -z-0">
          <div className="absolute inset-0 bg-hero-ambient" style={{ backgroundImage: "url(/visuals/hero-ambient.png)" }} />
          <div className="absolute -top-40 -right-40 w-[45vw] h-[45vw] rounded-full bg-[var(--gold)] opacity-[0.05] blur-[140px]" />
        </div>
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-16 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-5">
                · {t("about.label")}
              </div>
              <h1 className="font-display text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[1.05] tracking-[-0.035em]">
                {t("about.titleA")} <span className="font-serif gold-grad">{t("about.titleAccent")}</span>{t("about.titleEnd")}
                <br />
                {t("about.titleSub")}
              </h1>
              <p className="mt-8 text-lg lg:text-xl text-[var(--text-1)] max-w-2xl leading-relaxed">
                {tl(profile.intro)}
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to={pathWithLang("/kontakt")} className="btn-primary" data-testid="about-book-btn">
                  {t("about.buttonTalk")} <ArrowUpRight size={14} />
                </Link>
                <Link to={pathWithLang("/erfahrung")} className="btn-ghost text-xs" data-testid="about-cv-btn">
                  {t("about.buttonCv")}
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="portrait-frame aspect-[4/5] max-w-md mx-auto">
                <img
                  src="/karim-portrait.webp"
                  alt="Karim Benna"
                  className="w-full h-full object-contain object-bottom"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROFILE FACTS */}
      <section className="border-y hairline bg-[var(--bg-1)]/40">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          <Fact icon={Briefcase} label={t("about.experience")} value={t("exp.yearsExpValue")} />
          <Fact icon={Award} label={t("about.certificates")} value={`${certifications.length}`} />
          <Fact icon={Heart} label={t("about.birthday")} value={profile.birth_date || "—"} />
          <Fact icon={Users} label={t("about.bornIn")} value={tl(profile.birth_place) || "—"} />
          <Fact icon={MapPin} label={t("about.location")} value={tl(profile.location)} />
          <Fact icon={Languages} label={t("about.languages")} value={`${(profile.languages || []).length}`} />
        </div>
      </section>

      {/* LANGUAGES */}
      {profile.languages?.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
                · {t("about.langSectionLabel")}
              </div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                {t("about.langTitleA")} <span className="font-serif gold-grad">{t("about.langTitleAccent")}</span>{t("about.langTitleEnd")}
              </h2>
              <p className="text-[var(--text-1)] mt-5 max-w-md leading-relaxed">
                {t("about.langDesc")}
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              {profile.languages.map((l, i) => {
                const name = tl(l.name);
                const level = tl(l.level);
                // Detect native via DE canonical value
                const isNative = (l.level?.de || "").toLowerCase().includes("mutter");
                return (
                  <div
                    key={i}
                    className="svc-card flex items-center justify-between gap-3"
                  >
                    <div>
                      <div className="text-[var(--text-0)] font-display text-xl">{name}</div>
                      {level && (
                        <div className="text-sm text-[var(--text-2)] mt-1">{level}</div>
                      )}
                    </div>
                    {isNative && (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 border rounded-full bg-[var(--gold)]/10 border-[var(--gold)]/40 text-[var(--gold)]">
                        {t("about.native")}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* MOTTOS */}
      <section className="border-t hairline bg-[var(--bg-1)]/30">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
                · {t("about.principlesLabel")}
              </div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                {t("about.principlesTitleA")} <span className="font-serif gold-grad">{t("about.principlesAccent")}</span>{t("about.principlesEnd")}
              </h2>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 gap-3">
              {profile.mottos?.map((m, i) => (
                <div key={i} className="svc-card flex items-start gap-5 py-5">
                  <div className="text-2xl font-serif gold-grad shrink-0">
                    0{i + 1}
                  </div>
                  <p className="font-serif text-xl lg:text-2xl text-[var(--text-0)] leading-snug">
                    „{tl(m)}“
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS (preview) */}
      {certifications.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
          <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
                · {t("about.certsLabel")}
              </div>
              <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                {t("about.certsTitleA")} <span className="font-serif gold-grad">{t("about.certsAccent")}</span>{t("about.certsEnd")}
              </h2>
            </div>
            <Link to={pathWithLang("/erfahrung")} className="btn-ghost text-xs self-start md:self-end" data-testid="about-cert-cta">
              {t("about.allStations")} <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {certifications.map((c) => {
              const Icon = CERT_ICONS[c.icon] || Award;
              return (
                <div key={c.key} data-testid={`cert-${c.key}`} className="svc-card themed-card flex items-start gap-5">
                  <div
                    aria-hidden="true"
                    className="themed-card-backdrop"
                    style={{ backgroundImage: "url(/visuals/cards/certs-parchment.png)" }}
                  />
                  <div aria-hidden="true" className="themed-card-overlay" />
                  <div className="w-14 h-14 rounded bg-[var(--gold)]/10 border hairline-gold flex items-center justify-center shrink-0 backdrop-blur-sm">
                    <Icon size={22} className="text-[var(--gold)]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-mono text-xs text-[var(--gold)] uppercase tracking-wider mb-1">
                      {c.year}
                    </div>
                    <h3 className="font-display text-xl text-[var(--text-0)] leading-snug">{tl(c.name)}</h3>
                    <div className="text-sm text-[var(--text-2)] mt-1">{tl(c.issuer)}</div>
                    {c.pdf_url && (
                      <a
                        href={c.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        data-testid={`cert-download-${c.key}`}
                        className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-[var(--gold)] hover:text-[var(--text-0)] transition-colors duration-200 group"
                      >
                        <Download size={14} className="transition-transform duration-200 group-hover:translate-y-0.5" />
                        <span>{t("about.downloadCert")}</span>
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* CORE SKILLS */}
      <section className="border-y hairline bg-[var(--bg-1)]/40">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
              · {t("about.coreLabel")}
            </div>
            <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              {t("about.coreTitleA")} <span className="font-serif gold-grad">{t("about.coreAccent")}</span> {t("about.coreEnd")}
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-3">
            {profile.core_skills?.map((s, i) => (
              <li key={i} className="svc-card flex items-center gap-4 py-5">
                <Check size={18} className="text-[var(--gold)] shrink-0" />
                <span className="text-lg lg:text-xl text-[var(--text-0)] font-medium">{tl(s)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* AVAILABILITY */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <InfoBlock label={t("about.availability")} value={tl(profile.availability)} />
          <InfoBlock label={t("about.workMode")} value={tl(profile.flexibility)} />
          <InfoBlock label={t("about.nationality")} value={tl(profile.nationality) || "—"} />
        </div>
      </section>

      {/* HOBBIES */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
        <div className="mb-10 max-w-2xl">
          <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
            · {t("about.behindLabel")}
          </div>
          <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">
            {t("about.behindA")} <span className="font-serif gold-grad">{t("about.behindAccent")}</span>{t("about.behindEnd")}
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {profile.hobbies?.map((h, i) => {
            const key = h?.de || (typeof h === "string" ? h : "");
            const Icon = HOBBY_ICONS[key] || Music2;
            return (
              <div key={i} className="svc-card flex flex-col items-center text-center gap-3 p-5">
                <Icon size={22} className="text-[var(--gold)]" />
                <div className="text-[var(--text-0)] text-sm leading-tight">{tl(h)}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 pb-24">
        <div className="border hairline-gold rounded p-10 lg:p-14 flex flex-col lg:flex-row gap-8 items-center justify-between">
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight leading-tight max-w-2xl">
            {t("about.matchTitle")} <span className="font-serif gold-grad">{t("about.matchAccent")}</span>{t("about.matchEnd")}
          </h2>
          <Link to={pathWithLang("/kontakt")} className="btn-primary" data-testid="about-final-cta">
            {t("about.finalCta")} <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded bg-[var(--gold)]/10 border hairline-gold flex items-center justify-center shrink-0">
        <Icon size={16} className="text-[var(--gold)]" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[var(--text-2)] font-mono mb-0.5">{label}</div>
        <div className="text-[var(--text-0)] text-sm font-medium truncate">{value}</div>
      </div>
    </div>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div className="svc-card">
      <div className="text-xs uppercase tracking-wider text-[var(--gold)] font-medium mb-3">{label}</div>
      <div className="text-[var(--text-0)] text-lg leading-snug">{value}</div>
    </div>
  );
}
